import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type {
  ApiResponse,
  ChangePlanPayload,
  ChangePlanRequest,
  PaymentDto,
  RefundPaymentRequest,
  VoidPaymentRequest,
} from "@membentry/shared-types";
import type {
  CreatePaymentRequest,
  MarkPaymentRequest,
  MarkUnpaidRequest,
  PaymentListParams,
  RevenueReportDto,
  PaymentResponseDto,
  UserPaymentSummaryDto,
} from "@membentry/shared-api";
import type { AxiosInstance } from "axios";
import {
  CLUB_MEMBERS_QUERY_KEY,
  CLUB_MEMBERSHIP_QUERY_KEY,
} from "../clubs/queryKeys";
import {
  createInfiniteListQuery,
  type InfiniteListOptions,
} from "../common/infinite-list";
import {
  invalidateMemberHistory,
  VALIDATE_MEMBERSHIP_QUERY_KEY,
} from "../members/useMemberHooks";

type UserPaymentSummaryParams = {
  from?: string;
  to?: string;
  search?: string;
  page?: number;
  pageSize?: number;
};

export const PAYMENTS_QUERY_KEY = "payments";
export const PAYMENT_QUERY_KEY = "payment";

type MutationSuccessHandler<TData, TVariables> = NonNullable<
  UseMutationOptions<TData, Error, TVariables>["onSuccess"]
>;

const withOnSuccess = <TData, TVariables>(
  base: MutationSuccessHandler<TData, TVariables>,
  extra?: MutationSuccessHandler<TData, TVariables>,
): MutationSuccessHandler<TData, TVariables> => {
  return (data, variables, onMutateResult, context) => {
    base(data, variables, onMutateResult, context);
    return extra?.(data, variables, onMutateResult, context);
  };
};

const invalidatePaymentMemberHistory = (
  queryClient: ReturnType<typeof useQueryClient>,
  payment?: PaymentDto | null,
) => {
  if (payment?.clubId && payment.userId) {
    invalidateMemberHistory(queryClient, payment.clubId, payment.userId);
  }
};

const getRevenueQueryKey = (opts?: { from?: string; to?: string }) =>
  [PAYMENTS_QUERY_KEY, "revenue", opts?.from, opts?.to] as const;

const getUnpaidPaymentsQueryKey = (params?: PaymentListParams) =>
  [
    PAYMENTS_QUERY_KEY,
    "unpaid",
    params?.from,
    params?.to,
    params?.status,
    params?.userId,
    params?.page,
    params?.pageSize,
    params?.bucket,
  ] as const;

const getUserPaymentSummariesQueryKey = (opts?: UserPaymentSummaryParams) =>
  [
    PAYMENTS_QUERY_KEY,
    "user-summaries",
    opts?.from,
    opts?.to,
    opts?.search,
    opts?.page,
    opts?.pageSize,
  ] as const;

const getPaymentQueryKey = (paymentId?: string) =>
  [PAYMENT_QUERY_KEY, paymentId] as const;

export function useCreatePaymentMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<ApiResponse<PaymentDto>, Error, { membershipId: string | null; req: CreatePaymentRequest; idempotencyKey?: string }>,
    "mutationFn"
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ membershipId, req, idempotencyKey }: { membershipId: string | null; req: CreatePaymentRequest; idempotencyKey?: string }) => {
      const { createPayment } = await import("@membentry/shared-api");
      return idempotencyKey
        ? createPayment(client, membershipId, req, { idempotencyKey })
        : createPayment(client, membershipId, req);
    },
    ...options,
    onSuccess: withOnSuccess((data) => {
      queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY, "revenue"] });
      queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY, "unpaid"] });
      queryClient.invalidateQueries({
        queryKey: [PAYMENTS_QUERY_KEY, "user-summaries"],
      });
      invalidatePaymentMemberHistory(queryClient, data.payload);
    }, options?.onSuccess),
  });
}

export function useMarkPaidMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<ApiResponse<PaymentDto>, Error, { paymentId: string; req: MarkPaymentRequest }>,
    "mutationFn"
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ paymentId, req }: { paymentId: string; req: MarkPaymentRequest }) => {
      const { markPaid } = await import("@membentry/shared-api");
      return markPaid(client, paymentId, req);
    },
    ...options,
    onSuccess: withOnSuccess((data) => {
      queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY, "revenue"] });
      queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY, "unpaid"] });
      queryClient.invalidateQueries({
        queryKey: [PAYMENTS_QUERY_KEY, "user-summaries"],
      });
      queryClient.invalidateQueries({
        queryKey: getPaymentQueryKey(data.payload?.id),
      });
      invalidatePaymentMemberHistory(queryClient, data.payload);
    }, options?.onSuccess),
  });
}

export function useMarkUnpaidMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<ApiResponse<PaymentDto>, Error, { paymentId: string; req: MarkUnpaidRequest }>,
    "mutationFn"
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ paymentId, req }: { paymentId: string; req: MarkUnpaidRequest }) => {
      const { markUnpaid } = await import("@membentry/shared-api");
      return markUnpaid(client, paymentId, req);
    },
    ...options,
    onSuccess: withOnSuccess((data) => {
      queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY, "revenue"] });
      queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY, "unpaid"] });
      queryClient.invalidateQueries({
        queryKey: [PAYMENTS_QUERY_KEY, "user-summaries"],
      });
      queryClient.invalidateQueries({
        queryKey: getPaymentQueryKey(data.payload?.id),
      });
      invalidatePaymentMemberHistory(queryClient, data.payload);
    }, options?.onSuccess),
  });
}

const invalidatePaymentSurfaces = (
  queryClient: ReturnType<typeof useQueryClient>,
  payment?: PaymentDto | null,
) => {
  queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY, "revenue"] });
  queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY, "unpaid"] });
  queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY, "user-summaries"] });
  queryClient.invalidateQueries({ queryKey: getPaymentQueryKey(payment?.id) });
  invalidatePaymentMemberHistory(queryClient, payment);
};

export function useRefundPaymentMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<ApiResponse<PaymentDto>, Error, { paymentId: string; req: RefundPaymentRequest }>,
    "mutationFn"
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ paymentId, req }: { paymentId: string; req: RefundPaymentRequest }) => {
      const { refundPayment } = await import("@membentry/shared-api");
      return refundPayment(client, paymentId, req);
    },
    ...options,
    onSuccess: withOnSuccess((data) => {
      invalidatePaymentSurfaces(queryClient, data.payload);
    }, options?.onSuccess),
  });
}

export function useVoidPaymentMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<ApiResponse<PaymentDto>, Error, { paymentId: string; req: VoidPaymentRequest }>,
    "mutationFn"
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ paymentId, req }: { paymentId: string; req: VoidPaymentRequest }) => {
      const { voidPayment } = await import("@membentry/shared-api");
      return voidPayment(client, paymentId, req);
    },
    ...options,
    onSuccess: withOnSuccess((data) => {
      invalidatePaymentSurfaces(queryClient, data.payload);
    }, options?.onSuccess),
  });
}

export type ChangePlanVariables = ChangePlanRequest & { idempotencyKey?: string };

export function useChangePlanMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<ApiResponse<ChangePlanPayload>, Error, ChangePlanVariables>,
    "mutationFn"
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ idempotencyKey, ...request }: ChangePlanVariables) => {
      const { changePlan } = await import("@membentry/shared-api");
      const { changePlanSchema } = await import("@membentry/shared-validations");
      const parsed = changePlanSchema.parse(request) as ChangePlanRequest;
      return idempotencyKey
        ? changePlan(client, parsed, { idempotencyKey })
        : changePlan(client, parsed);
    },
    ...options,
    onSuccess: withOnSuccess((_data, variables) => {
      invalidatePayments(queryClient);
      queryClient.invalidateQueries({ queryKey: [VALIDATE_MEMBERSHIP_QUERY_KEY, variables.userId] });
      queryClient.invalidateQueries({ queryKey: [CLUB_MEMBERS_QUERY_KEY, variables.clubId] });
      queryClient.invalidateQueries({ queryKey: [CLUB_MEMBERSHIP_QUERY_KEY, variables.clubId] });
      invalidateMemberHistory(queryClient, variables.clubId, variables.userId);
    }, options?.onSuccess),
  });
}

export function useGetPaymentQuery(
  client: AxiosInstance,
  paymentId: string | undefined,
  options?: Omit<UseQueryOptions<ApiResponse<PaymentDto>>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: getPaymentQueryKey(paymentId),
    queryFn: async () => {
      const { getPayment } = await import("@membentry/shared-api");
      return getPayment(client, paymentId!);
    },
    enabled: !!paymentId,
    ...options,
  });
}

export function useRevenueReportQuery(
  client: AxiosInstance,
  opts?: { from?: string; to?: string },
  options?: Omit<UseQueryOptions<ApiResponse<RevenueReportDto>>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: getRevenueQueryKey(opts),
    queryFn: async () => {
      const { getRevenueReport } = await import("@membentry/shared-api");
      return getRevenueReport(client, opts);
    },
    ...options,
  });
}

export function useListUnpaidPaymentsQuery(
  client: AxiosInstance,
  params?: PaymentListParams,
  options?: Omit<UseQueryOptions<ApiResponse<PaymentResponseDto[]>>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: getUnpaidPaymentsQueryKey(params),
    queryFn: async () => {
      const { listUnpaidPayments } = await import("@membentry/shared-api");
      return listUnpaidPayments(client, params);
    },
    ...options,
  });
}

export const CLUB_PAYMENTS_INFINITE_PAGE_SIZE = 20;

export type ClubPaymentsInfiniteParams = Omit<PaymentListParams, "page">;

const useClubPaymentsInfiniteList = createInfiniteListQuery<
  ClubPaymentsInfiniteParams,
  PaymentResponseDto,
  readonly unknown[]
>({
  pageSize: (params) => params.pageSize ?? CLUB_PAYMENTS_INFINITE_PAGE_SIZE,
  buildQueryKey: (params, pageSize) =>
    [
      PAYMENTS_QUERY_KEY,
      "unpaid",
      "infinite",
      params.bucket,
      params.from,
      params.to,
      params.status,
      params.userId,
      pageSize,
    ] as const,
  fetchPage: async (client, params, { page, pageSize }) => {
    const { listUnpaidPayments } = await import("@membentry/shared-api");
    return listUnpaidPayments(client, { ...params, page, pageSize });
  },
});

export function useListClubPaymentsInfiniteQuery(
  client: AxiosInstance,
  params: ClubPaymentsInfiniteParams,
  options?: InfiniteListOptions<PaymentResponseDto, readonly unknown[]>,
) {
  return useClubPaymentsInfiniteList(client, params, options);
}

export function useUserPaymentSummariesQuery(
  client: AxiosInstance,
  opts?: UserPaymentSummaryParams,
  options?: Omit<UseQueryOptions<ApiResponse<UserPaymentSummaryDto[]>>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: getUserPaymentSummariesQueryKey(opts),
    queryFn: async () => {
      const { getUserPaymentSummaries } = await import("@membentry/shared-api");
      return getUserPaymentSummaries(client, opts);
    },
    ...options,
  });
}

export function invalidatePayments(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: [PAYMENTS_QUERY_KEY] });
}

export function invalidateRevenue(
  queryClient: ReturnType<typeof useQueryClient>,
  opts?: { from?: string; to?: string },
) {
  queryClient.invalidateQueries({
    queryKey: getRevenueQueryKey(opts),
    exact: false,
  });
}

export function invalidateUnpaidPayments(
  queryClient: ReturnType<typeof useQueryClient>,
  params?: PaymentListParams,
) {
  queryClient.invalidateQueries({
    queryKey: getUnpaidPaymentsQueryKey(params),
    exact: false,
  });
}

export function invalidateUserPaymentSummaries(
  queryClient: ReturnType<typeof useQueryClient>,
  opts?: UserPaymentSummaryParams,
) {
  queryClient.invalidateQueries({
    queryKey: getUserPaymentSummariesQueryKey(opts),
    exact: false,
  });
}

export function invalidatePayment(queryClient: ReturnType<typeof useQueryClient>, paymentId?: string) {
  queryClient.invalidateQueries({
    queryKey: getPaymentQueryKey(paymentId),
    exact: false,
  });
}
